/**
 * Pooled bullet fired by the boss's attack patterns. Separate from the
 * player's `Projectile` (different pool, different collision target) but
 * the same pooling idea — bullet-hell patterns can spawn 50+ of these in a
 * single burst, so zero per-shot allocation matters even more here.
 *
 * Supports optional homing (limited turn-rate steering toward a target),
 * `growPerDistance` (radius grows the farther it's traveled since spawn —
 * a bullet that looks harmless up close and fills more of the safe lane the
 * longer you leave it alone), and `accel` (speeds up or slows down over its
 * lifetime instead of a constant velocity throughout).
 *
 * BOSS-REWORK.md §B3 adds the three motion verbs the library was missing, so
 * the sixty patterns already authored gain vocabulary without a sixty-first
 * being written:
 *
 * - `omega` — constant angular velocity (rad/s) applied to the heading. Turns
 *   any straight-line spawn into an arc, a hook or a spiral.
 * - `holdFor` / `launchSpeed` — delayed launch. The bullet parks where it
 *   spawned as a visible shape for `holdFor` seconds, then leaves along its
 *   spawn heading at `launchSpeed` (danmaku's most-used single device; `accel`
 *   already existed and nothing ever spawned at rest to use it).
 * - `splitAfter` / `splitInto` — sub-munitions, resolved by BulletPool because
 *   only the pool can spawn. The parent is CONSUMED by its split
 *   (`splitKeepsParent` opts out), which is §B3's budget rule: density comes
 *   out of the pattern's allowance, never on top of it.
 *
 * `shootable` (§B9) gives a bullet HP so a card can pose a question the player
 * answers with the fire button — see CombatSystem's hostile-pool pass.
 */
export class BossBullet {
  constructor() {
    this.active = false;
    this.reset();
  }

  reset() {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.speed = 0;
    this.radius = 2.5;
    this._baseRadius = 2.5;
    this.damage = 5;
    this.color = '#ff5d6c';
    this.lifetime = 0;
    this.homing = false;
    this.turnRate = 0; // radians/sec max turn while homing
    this.target = null;
    this.growPerDistance = 0; // radius added per world-unit traveled since spawn
    this.accel = 0; // px/sec^2 added to speed each tick (negative = slows down)
    // --- §B3 motion verbs ---
    this.omega = 0; // rad/sec added to the heading each tick (curve/hook/spiral)
    this.holdFor = 0; // seconds parked at the spawn point before launching
    this.launchSpeed = 0; // speed to leave at once the hold expires
    this.splitAfter = 0; // seconds until this bullet becomes `splitInto` children (0 = never)
    this.splitInto = 0;
    this.splitSpread = Math.PI / 3; // total fan the children are dealt across
    this.splitSpeedMultiplier = 1;
    this.splitKeepsParent = false;
    this.splitPending = false; // read (and cleared) by BulletPool.update, which owns spawning
    this._heldFor = 0;
    this._splitTimer = 0;
    // §B9 — answerable bullets. `hp > 0` makes this bullet a target the
    // player's own shots can destroy; `shootable` is the cheap flag the
    // collision pass filters on so ordinary bullets cost one boolean.
    this.shootable = false;
    this.hp = 0;
    this._distanceTraveled = 0;
    this.prevX = 0;
    this.prevY = 0;
    this.trail = { length: 10, width: 1.5, alpha: 0.42 };
    // Once-per-bullet graze latch — set the first time this bullet passes
    // within the graze band (SpawningSystem._checkPlayerCollisions) so a
    // bullet skimming the player for several ticks pays out only once.
    this.grazed = false;
    // Set while this bullet is inside the band but has not yet earned the
    // graze. The payout is deferred until it LEAVES the band without
    // connecting, so a bullet that grazes on its approach and then hits pays
    // nothing — see SpawningSystem._checkPlayerCollisions.
    this.grazePending = false;
    // Once-per-bullet absorb latch (BULLET-HELL.md §D7). A matched-tone bullet
    // is NOT consumed when it washes through the player, so without a latch it
    // would pay resonance on every tick it overlapped.
    this.absorbed = false;
    // Origin stamp for the death recap — the boss pattern's name or the
    // minion type that fired it. Written by BulletPool.fire() from its
    // sourceLabel, never by the pattern itself.
    this.source = null;
  }

  spawn({
    x, y, vx, vy, radius = 2.5, damage = 5, color = '#ff5d6c', lifetime = 4,
    homing = false, turnRate = 2, target = null, growPerDistance = 0, accel = 0,
    tone = null, mismatchBonus = 0,
    omega = 0, holdFor = 0, launchSpeed = 0,
    splitAfter = 0, splitInto = 0, splitSpread = Math.PI / 3,
    splitSpeedMultiplier = 1, splitKeepsParent = false,
    shootable = false, hp = 0,
  }) {
    this.active = true;
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.speed = Math.hypot(vx, vy);
    this.radius = radius;
    this._baseRadius = radius;
    this.damage = damage;
    this.color = color;
    this.lifetime = lifetime;
    this.homing = homing;
    this.turnRate = turnRate;
    this.target = target;
    this.growPerDistance = growPerDistance;
    this.accel = accel;
    // Act II — The Antiphon. `tone` ('warm'/'cold', or null for a normal
    // bullet that always hits) drives the polarity rule in
    // SpawningSystem._checkPlayerCollisions; `mismatchBonus` is the extra
    // damage fraction when the player is attuned to the opposite tone.
    this.tone = tone;
    this.mismatchBonus = mismatchBonus;
    this.omega = omega;
    // Delayed launch: the spawn velocity is treated as a HEADING, parked at
    // zero speed for the hold, then re-applied at `launchSpeed` (defaulting to
    // whatever speed was passed in, so a caller only has to add `holdFor`).
    this.holdFor = holdFor;
    this._launchAngle = Math.atan2(vy, vx);
    this.launchSpeed = launchSpeed || this.speed;
    this._heldFor = 0;
    if (holdFor > 0) {
      this.vx = 0;
      this.vy = 0;
      this.speed = 0;
    }
    this.splitAfter = splitAfter;
    this.splitInto = splitInto;
    this.splitSpread = splitSpread;
    this.splitSpeedMultiplier = splitSpeedMultiplier;
    this.splitKeepsParent = splitKeepsParent;
    this.splitPending = false;
    this._splitTimer = splitAfter;
    this.shootable = shootable;
    this.hp = hp;
    this._distanceTraveled = 0;
    this.prevX = x;
    this.prevY = y;
    this.trail = { length: Math.min(22, Math.max(8, Math.hypot(vx, vy) * 0.08)), width: Math.max(1, radius * 0.7), alpha: 0.42 };
    this.grazed = false; // spawn() is the pool-recycle path, so the latch must clear here, not just in reset()
    this.grazePending = false; // ditto — a recycled bullet must not inherit an unpaid graze
    this.absorbed = false; // ditto — a recycled bullet must be absorbable again
    return this;
  }

  update(dt) {
    this.prevX = this.x;
    this.prevY = this.y;

    // Delayed launch (§B3). A held bullet is a shape on the board, not a
    // threat in motion — it still collides, which is the whole tension of the
    // device, it just doesn't move until the hold expires.
    if (this.holdFor > 0) {
      this._heldFor += dt;
      if (this._heldFor < this.holdFor) {
        this.lifetime -= dt;
        if (this.lifetime <= 0) this.active = false;
        return;
      }
      this.holdFor = 0;
      this.speed = this.launchSpeed;
      this.vx = Math.cos(this._launchAngle) * this.speed;
      this.vy = Math.sin(this._launchAngle) * this.speed;
    }

    if (this.homing && this.target) this._steerToward(this.target, dt);

    // Curve (§B3). Applied AFTER homing so a bullet given both still steers
    // toward its target and orbits it, rather than the turn being overwritten.
    if (this.omega !== 0) {
      const angle = Math.atan2(this.vy, this.vx) + this.omega * dt;
      this.vx = Math.cos(angle) * this.speed;
      this.vy = Math.sin(angle) * this.speed;
    }

    if (this.accel !== 0) {
      this.speed = Math.max(0, this.speed + this.accel * dt);
      const angle = Math.atan2(this.vy, this.vx);
      this.vx = Math.cos(angle) * this.speed;
      this.vy = Math.sin(angle) * this.speed;
    }

    const dx = this.vx * dt;
    const dy = this.vy * dt;
    this.x += dx;
    this.y += dy;

    if (this.growPerDistance !== 0) {
      this._distanceTraveled += Math.hypot(dx, dy);
      this.radius = this._baseRadius + this.growPerDistance * this._distanceTraveled;
    }

    // Sub-munitions (§B3): the bullet only RAISES the flag. BulletPool.update
    // reads it and does the spawning, because the bullet has no pool to spawn
    // from and handing it one would make every bullet know about the pool.
    if (this.splitAfter > 0 && !this.splitPending) {
      this._splitTimer -= dt;
      if (this._splitTimer <= 0) this.splitPending = true;
    }

    this.lifetime -= dt;
    if (this.lifetime <= 0) this.active = false;
  }

  /**
   * Player damage against an answerable bullet (§B9). Returns true on the hit
   * that destroys it, so the caller can pay the cancel drop exactly once.
   */
  takeDamage(amount) {
    if (!this.shootable || !this.active) return false;
    this.hp -= amount;
    if (this.hp > 0) return false;
    this.active = false;
    return true;
  }

  _steerToward(target, dt) {
    const desired = Math.atan2(target.y - this.y, target.x - this.x);
    const current = Math.atan2(this.vy, this.vx);
    let diff = desired - current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const maxTurn = this.turnRate * dt;
    const turn = Math.max(-maxTurn, Math.min(maxTurn, diff));
    const newAngle = current + turn;
    this.vx = Math.cos(newAngle) * this.speed;
    this.vy = Math.sin(newAngle) * this.speed;
  }
}
